import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { carBrands } from "../data/carBrands";
import { findModelImage, getHighResImage, isSimilarModelImage } from "../utils/imageMatching";

interface CarModelData {
  id: string;
  name: string;
  fuel?: string;
  power?: string;
  trunk?: string;
  price?: string;
  length?: string;
  width?: string;
  height?: string;
  [key: string]: string | boolean | undefined;
}

interface SelectedModel {
  brand: string;
  brandId: string;
  model: CarModelData;
}

interface SpecDef {
  key: keyof CarModelData;
  label: string;
  unit: string;
  higherIsBetter?: boolean;
  isText?: boolean;
  isPrice?: boolean;
}

const SPECS: SpecDef[] = [
  { key: "fuel",   label: "Combustible", unit: "",   isText: true },
  { key: "power",  label: "Potència",    unit: "CV", higherIsBetter: true },
  { key: "trunk",  label: "Maleter",     unit: "L",  higherIsBetter: true },
  { key: "price",  label: "Preu",        unit: "",   higherIsBetter: false, isPrice: true },
  { key: "length", label: "Llargada",    unit: "mm" },
  { key: "width",  label: "Amplada",     unit: "mm" },
  { key: "height", label: "Alçada",      unit: "mm" },
];

const MAX_MODELS = 3;

export const Comparador = () => {
  const [selectedModels, setSelectedModels] = useState<SelectedModel[]>([]);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (activeSlot !== null) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [activeSlot]);

  // Fixed search: split query into words, match ALL words in "Brand Model" combined string
  const searchResults = useMemo(() => {
    const query = searchQuery.trim();
    if (query.length < 2) return [];

    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const results: SelectedModel[] = [];

    for (const brand of carBrands) {
      for (const model of brand.models) {
        const combined = `${brand.name} ${model.name}`.toLowerCase();
        if (words.every(w => combined.includes(w))) {
          results.push({ brand: brand.name, brandId: brand.id, model: model as CarModelData });
        }
      }
    }

    // Sort by relevance: brand name that starts with the first word wins
    const firstWord = words[0];
    results.sort((a, b) => {
      const scoreOf = (s: SelectedModel) => {
        if (s.brand.toLowerCase().startsWith(firstWord)) return 3;
        if (s.brand.toLowerCase().includes(firstWord)) return 2;
        if (s.model.name.toLowerCase().startsWith(firstWord)) return 1;
        return 0;
      };
      const diff = scoreOf(b) - scoreOf(a);
      if (diff !== 0) return diff;
      return `${a.brand} ${a.model.name}`.localeCompare(`${b.brand} ${b.model.name}`);
    });

    return results;
  }, [searchQuery]);

  const handleSelectModel = (selected: SelectedModel) => {
    if (activeSlot === null) return;
    setSelectedModels(prev => {
      const updated = [...prev];
      if (activeSlot < updated.length) {
        updated[activeSlot] = selected;
      } else {
        updated.push(selected);
      }
      return updated;
    });
    setActiveSlot(null);
    setSearchQuery("");
  };

  const handleRemoveModel = (index: number) => {
    setSelectedModels(prev => prev.filter((_, i) => i !== index));
    if (activeSlot === index) {
      setActiveSlot(null);
      setSearchQuery("");
    }
  };

  const handleOpenSlot = (slotIndex: number) => {
    if (slotIndex > selectedModels.length) return;
    setActiveSlot(prev => (prev === slotIndex ? null : slotIndex));
    setSearchQuery("");
  };

  const handleCancelSearch = () => {
    setActiveSlot(null);
    setSearchQuery("");
  };

  const getNumericValue = (model: CarModelData, key: keyof CarModelData): number => {
    const val = model[key];
    if (!val || typeof val !== "string") return 0;
    if (key === "power") return parseInt(val.split("/")[0].replace(/\D/g, "")) || 0;
    if (key === "price") return parseInt(val.replace(/[^\d]/g, "")) || 0;
    return parseInt(val.replace(/\D/g, "")) || 0;
  };

  const getBestIndex = (spec: SpecDef): number => {
    if (spec.isText || spec.higherIsBetter === undefined || selectedModels.length < 2) return -1;
    const values = selectedModels.map(sm => getNumericValue(sm.model, spec.key));
    if (!values.some(v => v > 0)) return -1;
    const validValues = values.filter(v => v > 0);
    if (spec.higherIsBetter) {
      const max = Math.max(...validValues);
      return values.filter(v => v === max).length > 1 ? -1 : values.indexOf(max);
    } else {
      const min = Math.min(...validValues);
      return values.filter(v => v === min).length > 1 ? -1 : values.indexOf(min);
    }
  };

  const formatValue = (model: CarModelData, spec: SpecDef): string => {
    const val = model[spec.key];
    if (!val || typeof val !== "string" || val.trim() === "") return "—";
    if (spec.isPrice) return val.includes("€") ? val : `${val} €`;
    if (spec.unit) return `${val} ${spec.unit}`;
    return val;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-5xl">

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-foreground">
            Comparador de models
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground font-light">
            Compara fins a {MAX_MODELS} models i descobreix les seves diferències.
          </p>
        </div>

        {/* Always-3 model slots — 3 cols on all screen sizes */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
          {Array.from({ length: MAX_MODELS }, (_, i) => {
            const sm = selectedModels[i];
            const isActive = activeSlot === i;
            const isLocked = i > selectedModels.length;

            if (sm) {
              const modelImage = findModelImage(sm.brandId, sm.model.id, false);
              const highResImage = getHighResImage(modelImage, 500);
              return (
                <div
                  key={i}
                  className={`relative bg-card border-2 rounded-xl sm:rounded-2xl overflow-hidden group transition-all ${
                    isActive ? "border-ring shadow-lg" : "border-border"
                  }`}
                >
                  <div className="relative w-full h-20 sm:h-36 bg-linear-to-br from-secondary to-muted flex items-center justify-center p-2 sm:p-4">
                    {isSimilarModelImage(sm.brandId, sm.model.id) && (
                      <span className="absolute left-2 top-2 z-10 rounded-full bg-background/90 px-2 py-0.5 text-[9px] font-medium text-muted-foreground">
                        Similar
                      </span>
                    )}
                    <img
                      src={highResImage}
                      alt={`${sm.brand} ${sm.model.name}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-2 sm:p-3.5">
                    <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wide truncate">
                      {sm.brand}
                    </p>
                    <h3 className="text-xs sm:text-sm font-bold text-foreground mt-0.5 mb-1 leading-tight line-clamp-2">
                      {sm.model.name}
                    </h3>
                    <p className="text-[10px] sm:text-xs text-muted-foreground truncate hidden sm:block">
                      {sm.model.fuel}
                    </p>
                    <button
                      onClick={() => handleOpenSlot(i)}
                      className={`mt-2 sm:mt-3 w-full text-[10px] sm:text-xs text-center py-1 sm:py-1.5 rounded-md sm:rounded-lg transition-colors ${
                        isActive
                          ? "bg-ring/10 text-ring border border-ring/30"
                          : "bg-secondary hover:bg-secondary/70 text-muted-foreground hover:text-foreground"
                      }`}
                      type="button"
                      aria-label={`Canviar ${sm.model.name}`}
                    >
                      {isActive ? "Cercant..." : "Canviar"}
                    </button>
                  </div>
                  {/* Remove button — always visible on mobile, hover on desktop */}
                  <button
                    onClick={() => handleRemoveModel(i)}
                    className="absolute top-1.5 right-1.5 sm:top-2.5 sm:right-2.5 p-1 sm:p-1.5 bg-background/80 backdrop-blur-sm border border-border rounded-full sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-background"
                    aria-label={`Eliminar ${sm.model.name} de la comparació`}
                    type="button"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              );
            }

            // Empty / locked slot
            return (
              <button
                key={i}
                onClick={() => !isLocked && handleOpenSlot(i)}
                disabled={isLocked}
                type="button"
                aria-label={isLocked ? `Slot ${i + 1} no disponible` : `Afegir model a la posició ${i + 1}`}
                className={`flex flex-col items-center justify-center gap-1.5 sm:gap-2.5 border-2 rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[130px] sm:min-h-[200px] transition-all ${
                  isActive
                    ? "border-ring bg-ring/5 shadow-lg cursor-default"
                    : isLocked
                    ? "border-border/40 bg-secondary/10 cursor-default opacity-40"
                    : "border-dashed border-border hover:border-foreground/40 hover:bg-secondary/30 bg-card cursor-pointer group"
                }`}
              >
                <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                  isActive
                    ? "bg-ring text-background"
                    : isLocked
                    ? "bg-secondary text-muted-foreground/60"
                    : "bg-secondary group-hover:bg-foreground group-hover:text-background"
                }`}>
                  {isLocked ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  )}
                </div>
                <p className={`text-[10px] sm:text-xs font-semibold text-center transition-colors leading-tight ${
                  isActive
                    ? "text-ring"
                    : isLocked
                    ? "text-muted-foreground/40"
                    : "text-muted-foreground group-hover:text-foreground"
                }`}>
                  {isActive ? "Cercant..." : isLocked ? `Model ${i + 1}` : "Afegir model"}
                </p>
              </button>
            );
          })}
        </div>

        {/* Search panel — full width, appears below slots */}
        {activeSlot !== null && (
          <div className="mb-6 bg-card border-2 border-ring rounded-2xl overflow-hidden shadow-lg">
            {/* Input */}
            <div className="relative flex items-center border-b border-border">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Cerca marca o model... (p. ex. BMW S, Golf, Cupra Born)"
                className="w-full pl-11 pr-28 py-3.5 bg-transparent text-foreground text-sm focus:outline-none placeholder:text-muted-foreground"
                aria-label="Cercar model per comparar"
              />
              <div className="absolute right-3 flex items-center gap-1.5">
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors p-1"
                    aria-label="Esborrar text"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={handleCancelSearch}
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-2.5 py-1 transition-colors"
                  aria-label="Cancel·lar cerca"
                >
                  Cancel·lar
                </button>
              </div>
            </div>

            {/* Results list */}
            <div className="max-h-64 overflow-y-auto">
              {searchQuery.trim().length < 2 ? (
                <div className="flex items-center gap-2 px-4 py-5 text-xs text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Escriu almenys 2 caràcters — pots buscar per marca, model o ambdós (ex: "BMW S5")
                </div>
              ) : searchResults.length === 0 ? (
                <div className="px-4 py-5 text-xs text-center text-muted-foreground">
                  Cap model trobat per "<span className="font-semibold text-foreground">{searchQuery}</span>"
                </div>
              ) : (
                <div className="py-1">
                  {searchResults.map((r, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectModel(r)}
                      className="w-full text-left px-4 py-2.5 hover:bg-secondary transition-colors flex items-center justify-between group/item"
                      type="button"
                    >
                      <span>
                        <span className="text-sm font-semibold text-foreground">{r.brand} </span>
                        <span className="text-sm text-foreground">{r.model.name}</span>
                        {r.model.fuel && (
                          <span className="text-xs text-muted-foreground ml-2">{r.model.fuel}</span>
                        )}
                      </span>
                      <svg
                        className="opacity-0 group-hover/item:opacity-60 transition-opacity shrink-0 ml-2"
                        xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hint when not enough models */}
        {selectedModels.length < 2 && activeSlot === null && (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">
              {selectedModels.length === 0
                ? "Selecciona almenys 2 models per veure la comparació d'especificacions"
                : "Afegeix un segon model per iniciar la comparació"}
            </p>
          </div>
        )}

        {/* Comparison table */}
        {selectedModels.length >= 2 && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-base font-bold text-foreground">Comparació d'especificacions</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="text-left px-3 sm:px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide w-24 sm:w-36">
                      Espec.
                    </th>
                    {selectedModels.map((sm, i) => (
                      <th
                        key={i}
                        className="text-center px-3 sm:px-6 py-3 text-xs font-semibold text-foreground uppercase tracking-wide"
                      >
                        <span className="hidden sm:inline">{sm.brand} </span>{sm.model.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {SPECS.map(spec => {
                    const bestIndex = getBestIndex(spec);
                    return (
                      <tr key={spec.key} className="hover:bg-secondary/20 transition-colors">
                        <td className="px-3 sm:px-6 py-3 sm:py-3.5 text-xs sm:text-sm text-muted-foreground font-medium whitespace-nowrap">
                          {spec.label}
                        </td>
                        {selectedModels.map((sm, i) => {
                          const displayVal = formatValue(sm.model, spec);
                          const isBest = i === bestIndex;
                          return (
                            <td
                              key={i}
                              className={`px-3 sm:px-6 py-3 sm:py-3.5 text-center text-xs sm:text-sm font-semibold ${
                                isBest
                                  ? "text-green-600 dark:text-green-400"
                                  : displayVal === "—"
                                  ? "text-muted-foreground font-normal"
                                  : "text-foreground"
                              }`}
                            >
                              {isBest && <span className="mr-1 text-xs">★</span>}
                              {displayVal}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-border bg-secondary/20">
              <p className="text-xs text-muted-foreground">★ Millor valor en aquesta especificació</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
